/**
 * PartEVO design tokens - the single place where colour values live.
 *
 * Everything else points here:
 *  - `_variables.scss` maps the SCSS variables to the CSS custom properties
 *    that `tokensToCssVariables()` emits on `:root` (see App.tsx);
 *  - `config/styles/colors.ts` and the MUI theme read the values directly,
 *    because MUI's palette needs real colours (it derives shades from them).
 *
 * Rule of thumb: no hex / rgb() literal outside this file. The only
 * exceptions are achromatic alpha overlays written inline (rgb(0 0 0 / x),
 * rgb(25 25 25 / x), rgb(255 255 255 / x)), which are depth, not palette.
 * Elevation comes from the two `shadow` tokens and every corner from the
 * `radius` tokens - no other shadow or radius value anywhere in the app.
 * Tinted backgrounds come from the three `surface*` tokens (or a semantic
 * `*Bg` for a status) - never from a neutral or blue tint directly.
 */
export const tokens = {
  color: {
    /* Brand */
    brandPrimary: '#0040d6',
    /* Hover / pressed shade of the brand blue (filled buttons) */
    brandPrimaryHover: '#0033ad',
    /* Neon accent - logo, hover underlines and small badges only. Never a
       text colour (white goes on the brand blue) and never a status colour
       (`success` below is the green for "in stock" / delivered). */
    brandAccent: '#00ff70',

    /* Neutrals (text, surfaces, borders) */
    neutral100: '#ffffff',
    neutral300: '#e8e8e8',
    neutral600: '#757575',
    neutral700: '#474747',
    neutral800: '#252525',
    neutral900: '#191919',

    /* Surfaces (design audit, section 3.5) - the only tinted backgrounds.
       surface1 = brand tint for anything that sits on white: cards, tiles,
       forms, menus, info boxes, table heads, disabled/selected rows.
       surface2 = neutral band for full-width sections between white ones
       (and loading placeholders).
       surfaceCarFinder = the "Find your car" form only: hero / listing
       widget, Choose Car modal, reg-plate box. Nothing else is green. */
    surface1: '#f2f5fd',
    surface2: '#f5f5f5',
    surfaceCarFinder: '#e6fff1',

    /* Blue tints (borders, dividers and interaction steps on surface1:
       blue200 = hover / darkened row / small round controls, blue300 =
       pressed or stronger border, blue400 = input border) */
    blue200: '#e6ecfb',
    blue300: '#ccd9f7',
    blue400: '#b3c6f3',
    blue900: '#3366de',

    /* Semantic - one colour per meaning. `success` is dark enough for white
       text on it and for itself as text on `successBg` (both >= 4.5:1). */
    success: '#0e813c',
    successBg: '#e6fff1',
    error: '#d80027',
    /* A solid tint, not an alpha overlay: `error` as text on it has to reach
       4.5:1 for the 12px status badges (the 20% red it replaced gave 3.7). */
    errorBg: '#fdeef0',
    /* Dark enough as text on `warningBg` (4.9:1 - the audit's #b76e00 only
       reached 3.7) for the checkout warning and the "pending" order badges. */
    warning: '#9a5c00',
    warningBg: '#fff4e0',
    info: '#0040d6',
    infoBg: '#f2f5fd',

    /* Text roles */
    text: '#191919',
    textSecondary: '#474747',
    textMuted: '#757575',
    textInverse: '#ffffff',

    /* UK registration plate (deliberate metaphor, kept) */
    regPlate: '#ffe14c',

    /* Achromatic overlays on imagery / backdrops */
    overlay30: 'rgb(25 25 25 / 30%)',
    overlay80: 'rgb(25 25 25 / 80%)',
  },
  /* Elevation (design audit, section 4.4): 1 = resting cards and panels,
     2 = floating surfaces - menus, popovers, dialogs, sticky bars and hover
     lifts. Achromatic on purpose: depth, not palette. */
  shadow: {
    1: '0 2px 12px rgb(25 25 25 / 6%)',
    2: '0 8px 24px rgb(25 25 25 / 12%)',
  },
  /* Corner radii: s = fields, badges, thumbnails and menu rows; m = buttons,
     cards, tiles, modals and menus; pill = status chips, counters and
     circles (dots, avatars, round icon buttons). */
  radius: {
    s: '4px',
    m: '8px',
    pill: '999px',
  },
} as const;

/**
 * Turns a camelCase token key into its CSS custom property name.
 * @param {string} group token group, e.g. `color`
 * @param {string} key camelCase token key, e.g. `brandPrimary`
 * @return {string} the property name, e.g. `--color-brand-primary`
 */
const toCssName = (group: string, key: string): string =>
  `--${group}-${key.replace(/([a-z])([A-Z0-9])/g, '$1-$2').toLowerCase()}`;

/**
 * `:root` declarations for every token, e.g. `--color-brand-primary`,
 * `--color-success-bg`, `--shadow-1`, `--radius-m`. Rendered once by App.tsx so the SCSS
 * modules (via `_variables.scss`) resolve the same values as the MUI theme.
 * @return {Record<string, string>} property name to value
 */
export const tokensToCssVariables = (): Record<string, string> => {
  const vars: Record<string, string> = {};
  (Object.keys(tokens) as Array<keyof typeof tokens>).forEach((group) => {
    Object.entries(tokens[group]).forEach(([key, value]) => {
      vars[toCssName(group, key)] = value;
    });
  });
  return vars;
};
